import { createSelector } from '@reduxjs/toolkit'
import { pipe, pluck } from 'ramda'
import getDataSelector, { getByIdSelector } from 'core/utils/getDataSelector'
import DataKeys from 'k8s/DataKeys'
import { Tenant } from 'api-client/keystone.model'
import createSorter, { SortConfig } from 'core/helpers/createSorter'
import { selectParamsFromProps, createSharedSelector } from 'core/utils/selectorHelpers'

export const filterOutDomains = (tenant) => !!tenant.domain_id

export const reservedTenantNames = ['admin', 'services', 'Default', 'heat']
export const filterValidTenants = (tenant) => !reservedTenantNames.includes(tenant.name)

export const allDomainTenantsSelector = createSharedSelector(
  getDataSelector<DataKeys.AllTenants>(DataKeys.AllTenants),
  getDataSelector<DataKeys.Domains>(DataKeys.Domains),
  (tenants, domains) => {
    return tenants.filter(filterValidTenants).map((tenant) => ({
      ...tenant,
      domain: domains.find((domain) => domain.id === tenant.domain_id)?.name || '',
    }))
  },
)

export const allTenantsSelector = createSharedSelector(
  getDataSelector<DataKeys.ManagementTenants>(DataKeys.ManagementTenants),
  (tenants) => {
    return tenants
  },
)

/**
 * O(1) lookup for management tenants by ID from normalized cache
 * Use this for direct ID-based lookups: selectManagementTenantsById(state)('tenant-123')
 */
export const managementTenantsById = getByIdSelector(DataKeys.ManagementTenants)

/**
 * @deprecated Use managementTenantsById for direct lookups
 * Kept for backward compatibility - transforms list to object map
 */
export const allTenantsSelectorByID = createSharedSelector(
  getDataSelector<DataKeys.ManagementTenants>(DataKeys.ManagementTenants),
  (tenants) => {
    return tenants.reduce((accum, tenant) => {
      return {
        ...accum,
        [tenant.id]: tenant,
      }
    }, {})
  },
)

export const tenantsSelector = createSharedSelector(
  allTenantsSelector,
  getDataSelector<DataKeys.Domains>(DataKeys.Domains),
  (allTenantsAllUsers, domains) => {
    return allTenantsAllUsers.filter(filterValidTenants).map((tenant) => ({
      ...tenant,
      domain: domains.find((domain) => domain.id === tenant.domain_id)?.name || '',
    }))
  },
)

/**
 * O(1) lookup for filtered tenants by ID from normalized cache
 * Contains only valid (non-reserved) tenants with domain information
 * Use this for direct ID-based lookups of filtered tenants
 */
export const tenantsById = createSharedSelector(tenantsSelector, (tenants) => {
  return tenants.reduce((accum, tenant) => {
    return {
      ...accum,
      [tenant.id]: tenant,
    }
  }, {})
})

/**
 * @deprecated Use tenantsById
 * Kept for backward compatibility
 */
export const tenantsByIdSelector = tenantsById

export const makeFilteredTenantsSelector = (
  defaultParams = {
    orderBy: 'created_at',
    orderDirection: 'desc',
  } as SortConfig,
) => {
  const selectParams = selectParamsFromProps(defaultParams)
  return createSelector(tenantsSelector, selectParams, (tenants, params) => {
    const { orderBy, orderDirection } = params
    return pipe<Tenant[], Tenant[]>(createSorter({ orderBy, orderDirection }))(tenants)
  })
}

export const userTenantsSelector = getDataSelector<DataKeys.UserTenants>(DataKeys.UserTenants)

/**
 * O(1) lookup for user's tenants by ID from normalized cache
 * Use this for direct ID-based lookups: selectUserTenantsById(state)('tenant-123')
 */
export const userTenantsById = getByIdSelector(DataKeys.UserTenants)

export const tenantRolesSelector = getDataSelector<DataKeys.ManagementTenantsRoleAssignments>(
  DataKeys.ManagementTenantsRoleAssignments,
  ['tenantId'],
)
